import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Custom hook encapsulating Service Menu CRUD.
 * Returns: { services, drafts, busy, load, updateDraft, saveOne, toggleActive, remove, addNew }
 * UI layer only renders. All API + state mutation lives here.
 */
export function useServiceMenu() {
    const [services, setServices] = useState([]);
    const [drafts, setDrafts] = useState({});
    const [busy, setBusy] = useState({});

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/services", { params: { include_inactive: true } });
            setServices(data);
            const ds = {};
            data.forEach((s) => {
                ds[s.service_id] = { price: s.price.toString(), duration_min: s.duration_min, name: s.name };
            });
            setDrafts(ds);
        } catch (err) {
            console.error("services load failed:", err);
            toast.error("Couldn't load services");
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const updateDraft = useCallback((id, field, val) => {
        setDrafts((d) => ({ ...d, [id]: { ...d[id], [field]: val } }));
    }, []);

    const withBusy = async (id, fn) => {
        setBusy((b) => ({ ...b, [id]: true }));
        try { await fn(); }
        finally { setBusy((b) => ({ ...b, [id]: false })); }
    };

    const saveOne = useCallback(async (id) => {
        const d = drafts[id];
        const priceNum = parseFloat(d.price);
        if (Number.isNaN(priceNum) || priceNum < 0) {
            toast.error("Price must be a number ≥ 0");
            return;
        }
        const durNum = parseInt(d.duration_min, 10);
        if (Number.isNaN(durNum) || durNum < 0) {
            toast.error("Duration must be a number");
            return;
        }
        await withBusy(id, async () => {
            try {
                await api.put(`/services/${id}`, { price: priceNum, duration_min: durNum, name: d.name });
                toast.success("Saved");
                await load();
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Update failed");
            }
        });
    }, [drafts, load]);

    const toggleActive = useCallback(async (s) => {
        await withBusy(s.service_id, async () => {
            try {
                await api.put(`/services/${s.service_id}`, { active: !s.active });
                await load();
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Toggle failed");
            }
        });
    }, [load]);

    const remove = useCallback(async (s) => {
        if (!window.confirm(`Hide "${s.name}" from the booking menu? It will be soft-deleted.`)) return;
        await withBusy(s.service_id, async () => {
            try {
                await api.delete(`/services/${s.service_id}`);
                await load();
                toast.success("Hidden from booking menu");
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Failed");
            }
        });
    }, [load]);

    const addNew = useCallback(async (svc) => {
        const priceNum = parseFloat(svc.price);
        if (!svc.service_id.trim() || !svc.name.trim() || Number.isNaN(priceNum)) {
            toast.error("Fill in id, name, and price");
            return false;
        }
        try {
            await api.post("/services", {
                service_id: svc.service_id.toLowerCase().replace(/[^a-z0-9_]/g, "_"),
                name: svc.name,
                category: svc.category,
                price: priceNum,
                duration_min: parseInt(svc.duration_min, 10) || 60,
                sort_order: 200,
            });
            toast.success("Added");
            await load();
            return true;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to add");
            return false;
        }
    }, [load]);

    return { services, drafts, busy, load, updateDraft, saveOne, toggleActive, remove, addNew };
}
